import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import QueryString from "qs";
import { ReqMethods, ghl } from "../../../enums";
import { useAppServices } from "../../../hook/services";
import env from "../../../config";

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

function GHL() {
  const { search } = useLocation();
  const code = new URLSearchParams(search).get("code");
  const AppServices = useAppServices();
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(STATUS.LOADING);

  const associate = async (creds) => {
    const payload = {
      modulesAuth: creds
    };

    setTimeout(async () => {
      const { response, error } = await AppServices.auth.integrate_customizer_module_app({
        payload,
      });
      if (error) {
        if (error?.code == "ERR_NETWORK") {
          setStatus(STATUS.WARNING);
          return setMessage("Saving credentials may take some time...");
        } else {
          setStatus(STATUS.ERROR);
          setMessage("Something went wrong while saving credentials");
        }
      } else {
        setStatus(STATUS.SUCCESS);
        setMessage("App integrated successfully!");
      }
    }, 5000);
  };

  const getCreds = async () => {
    const payload = {
      method: ReqMethods.POST,
      path: ghl.APIs.oauth,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      payload: QueryString.stringify({
        client_id: env.CUSTOMIZR_MODULE_GHL.CLIENT_ID,
        client_secret: env.CUSTOMIZR_MODULE_GHL.CLIENT_SECRET,
        grant_type: "authorization_code",
        code,
        refresh_token: "",
      }),
    };
    const { response } = await AppServices.services.ghl.call_service({
      payload,
    });

    if (!response) {
      setStatus(STATUS.ERROR);
      return setMessage(
        "Something went wrong during integration. Please try again later."
      );
    }

    return response && associate(response.data);
  };

  useEffect(() => {
    getCreds();
  }, []);

  const statusConfig = {
    [STATUS.LOADING]: {
      icon: null,
      color: "#38bdf8",
      bg: "#e0f2fe",
      border: "#7dd3fc",
    },
    [STATUS.SUCCESS]: {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      color: "#16a34a",
      bg: "#f0fdf4",
      border: "#bbf7d0",
    },
    [STATUS.WARNING]: {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
    },
    [STATUS.ERROR]: {
      icon: (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      color: "#dc2626",
      bg: "#fef2f2",
      border: "#fecaca",
    },
  };

  const current = statusConfig[status];

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(160deg, #080d1a 0%, #0c1a3d 40%, #111827 100%)",
        fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decorative elements */}
      <div
        style={{
          position: "absolute",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)",
          top: "-100px",
          right: "-100px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)",
          bottom: "-80px",
          left: "-80px",
          pointerEvents: "none",
        }}
      />

      {/* Main card */}
      <div
        style={{
          background: "rgba(255, 255, 255, 0.03)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "20px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 8px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)",
          padding: "48px 44px",
          maxWidth: "460px",
          width: "100%",
          textAlign: "center",
          animation: "slideUp 0.5s ease-out",
        }}
      >
        {/* Status icon */}
        <div
          style={{
            width: "88px",
            height: "88px",
            borderRadius: "50%",
            background: status === STATUS.LOADING
              ? "linear-gradient(135deg, rgba(56,189,248,0.15), rgba(139,92,246,0.08))"
              : current.bg,
            border: `2px solid ${status === STATUS.LOADING ? "rgba(56,189,248,0.3)" : current.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 28px",
            animation: status === STATUS.LOADING ? "pulse 2s ease-in-out infinite" : "scaleIn 0.4s ease-out",
          }}
        >
          {status === STATUS.LOADING ? (
            <div
              style={{
                width: "36px",
                height: "36px",
                border: "3px solid rgba(56,189,248,0.2)",
                borderTop: "3px solid #38bdf8",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
          ) : (
            current.icon
          )}
        </div>

        {/* Title */}
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: "24px",
            fontWeight: 700,
            color: "#ffffff",
            letterSpacing: "-0.3px",
          }}
        >
          {status === STATUS.LOADING
            ? "Setting Up Your App"
            : status === STATUS.SUCCESS
            ? "You're All Set!"
            : status === STATUS.WARNING
            ? "Almost There"
            : "Something Went Wrong"}
        </h2>

        {/* Description */}
        <p
          style={{
            margin: "0 0 28px",
            fontSize: "15px",
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.7,
          }}
        >
          {status === STATUS.LOADING
            ? "We're connecting your account and configuring everything. This will only take a moment..."
            : message}
        </p>

        {/* Loading progress bar */}
        {status === STATUS.LOADING && (
          <div
            style={{
              height: "3px",
              background: "rgba(255,255,255,0.06)",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "40%",
                background: "linear-gradient(90deg, #38bdf8, #8b5cf6, #38bdf8)",
                borderRadius: "4px",
                animation: "progress 1.8s ease-in-out infinite",
              }}
            />
          </div>
        )}

        {/* Loading steps indicator */}
        {status === STATUS.LOADING && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "24px",
              marginTop: "8px",
            }}
          >
            {["Authenticating", "Configuring", "Finalizing"].map((step, i) => (
              <div
                key={step}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.35)",
                  animation: `fadeIn 0.4s ease-out ${i * 0.15}s both`,
                }}
              >
                <div
                  style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: "rgba(56,189,248,0.6)",
                    animation: `dotPulse 1.5s ease-in-out ${i * 0.4}s infinite`,
                  }}
                />
                {step}
              </div>
            ))}
          </div>
        )}

        {/* Error retry button */}
        {status === STATUS.ERROR && (
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 32px",
              fontSize: "14px",
              fontWeight: 600,
              color: "#ffffff",
              background: "linear-gradient(135deg, #38bdf8, #8b5cf6)",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 16px rgba(56,189,248,0.3)",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 6px 20px rgba(56,189,248,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 16px rgba(56,189,248,0.3)";
            }}
          >
            Try Again
          </button>
        )}

        {/* Success action */}
        {status === STATUS.SUCCESS && (
          <div
            style={{
              fontSize: "13px",
              color: "rgba(255,255,255,0.4)",
              marginTop: "4px",
            }}
          >
            You can safely close this window.
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "32px",
          fontSize: "12px",
          color: "rgba(255,255,255,0.2)",
          animation: "fadeIn 0.8s ease-out",
        }}
      >
        Powered by CRM Customizer
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}

export default GHL;
